import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { requestRateRefresh } from "@redotech/redo-merchant-app-common/open-order-notifications";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoExternalLink } from "@redotech/redo-web/arbiter-components/links/redo-external-link";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";

interface UpsAgreementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccepted: () => void;
}

export const UpsAgreementModal = memo(function UpsAgreementModal({
  isOpen,
  onClose,
  onAccepted,
}: UpsAgreementModalProps) {
  const [accepted, setAccepted] = useState(false);
  const outboundLabelsRpcClient = useRequiredContext(
    RedoOutboundLabelsRpcClientContext,
  );

  const team = useRequiredContext(TeamContext);

  const handleSubmit = useHandler(async () => {
    if (!accepted) {
      return;
    }
    const result = await outboundLabelsRpcClient.acceptUpsAgreement({});
    // Open orders were rated without UPS; re-rate them with the progress toast.
    if (result.success) {
      requestRateRefresh(team._id.toString());
    }
    onAccepted();
    onClose();
  });

  return (
    <RedoModal
      isOpen={isOpen}
      onModalCloseRequested={onClose}
      title="UPS Service Agreement"
    >
      <Flex dir="column" gap="lg" p="sm">
        <RedoCheckbox
          description={
            <Text fontSize="xs" textColor="secondary">
              To start shipping with UPS, I am agreeing to the UPS Tariff /
              Terms and Conditions of Service. I will not attempt to ship any
              items prohibited by UPS, or any UPS-regulated items without
              express written contract with UPS. I will also agree to the UPS
              Technology Agreement.
            </Text>
          }
          setValue={(value: boolean) => setAccepted(value)}
          size="sm"
          value={accepted}
        />
        <Flex dir="column" gap="sm" pl="3xl">
          <RedoExternalLink
            text="UPS Tariff / Terms and Conditions of Service"
            url="https://assets.ups.com/adobe/assets/urn:aaid:aem:c6bf8a2f-018f-4aa0-838b-ffc1a75eb1d9/original/as/terms-carriage-us-en.pdf"
          />
          <RedoExternalLink
            text="List of Prohibited Articles for Shipping"
            url="https://www.ups.com/us/en/support/shipping-support/shipping-special-care-regulated-items/prohibited-items"
          />
          <RedoExternalLink
            text="UPS Technology Agreement and End User Rights"
            url="https://www.ups.com/assets/resources/webcontent/en_US/UTA.pdf"
          />
        </Flex>
      </Flex>
      <Flex dir="row" gap="md" justify="flex-end">
        <RedoButton
          disabled={!team}
          hierarchy="secondary"
          onClick={onClose}
          size="md"
          text="Cancel"
          theme="normal"
        />
        <RedoButton
          disabled={!accepted}
          hierarchy="primary"
          onClick={handleSubmit}
          size="md"
          text="Submit"
          theme="normal"
        />
      </Flex>
    </RedoModal>
  );
});
